import type {
  ProductPromotion,
  ProductPromotionGroupRow,
  ProductPromotionOptionRow,
  ProductPromotionRow,
  PromotionGroup,
  PromotionGroupMode,
  PromotionOption,
  PromotionSelectionInput,
  ResolvedPromotionComponent,
} from '@/types/promotions';

export class PromotionValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PromotionValidationError';
  }
}

function mergeResolvedByProduct(rows: ResolvedPromotionComponent[]): ResolvedPromotionComponent[] {
  const map = new Map<string, number>();
  for (const row of rows) {
    map.set(row.componentProductId, (map.get(row.componentProductId) ?? 0) + row.quantity);
  }
  return [...map.entries()].map(([componentProductId, quantity]) => ({ componentProductId, quantity }));
}

/**
 * Validates cashier/admin selections and returns **per-bundle** component units
 * (one parent `tab_items.quantity` = one bundle; stock uses `quantity * tab_items.quantity`).
 */
export function validateAndResolvePromotion(
  definition: ProductPromotion,
  inputs: PromotionSelectionInput[]
): ResolvedPromotionComponent[] {
  if (!definition.isActive) {
    throw new PromotionValidationError('Promotion is not active');
  }

  const inputByGroup = new Map(inputs.map((i) => [i.groupId, i]));
  const sortedGroups = [...definition.groups].sort((a, b) => a.id.localeCompare(b.id));
  const resolved: ResolvedPromotionComponent[] = [];

  for (const group of sortedGroups) {
    const allowed = new Set(group.options.map((o) => o.componentProductId));
    const sortedOptions = [...group.options].sort((a, b) => a.id.localeCompare(b.id));

    if (group.mode === 'fixed') {
      let hasDefaultQuantity = false;
      for (const opt of sortedOptions) {
        if (opt.defaultQuantity > 0) {
          if (!allowed.has(opt.componentProductId)) {
            throw new PromotionValidationError('Invalid fixed promotion option configuration');
          }
          resolved.push({
            componentProductId: opt.componentProductId,
            quantity: opt.defaultQuantity,
          });
          hasDefaultQuantity = true;
        }
      }
      if (!hasDefaultQuantity) {
        throw new PromotionValidationError(`Fixed promotion group "${group.id}" has no default quantities`);
      }
    } else if (group.mode === 'choose') {
      const input = inputByGroup.get(group.id);
      if (!input?.lines?.length) {
        throw new PromotionValidationError(`Missing selections for choose group "${group.id}"`);
      }
      let sum = 0;
      for (const line of input.lines) {
        if (line.quantity < 1) {
          throw new PromotionValidationError('Each chosen line must have quantity >= 1');
        }
        if (!allowed.has(line.productId)) {
          throw new PromotionValidationError(`Product is not allowed in choose group "${group.id}"`);
        }
        sum += line.quantity;
        resolved.push({ componentProductId: line.productId, quantity: line.quantity });
      }
      if (sum !== group.requiredUnits) {
        throw new PromotionValidationError(
          `Choose group "${group.id}" requires exactly ${group.requiredUnits} unit(s), got ${sum}`
        );
      }
    } else if (group.mode === 'same_product') {
      const input = inputByGroup.get(group.id);
      if (!input?.lines?.length) {
        throw new PromotionValidationError(`Missing selection for same-product group "${group.id}"`);
      }
      if (input.lines.length !== 1) {
        throw new PromotionValidationError(`Same-product group "${group.id}" requires exactly one line`);
      }
      const line = input.lines[0];
      if (!allowed.has(line.productId)) {
        throw new PromotionValidationError(`Product is not allowed in same-product group "${group.id}"`);
      }
      if (line.quantity !== group.requiredUnits) {
        throw new PromotionValidationError(
          `Same-product group "${group.id}" requires quantity ${group.requiredUnits}, got ${line.quantity}`
        );
      }
      resolved.push({ componentProductId: line.productId, quantity: line.quantity });
    } else {
      throw new PromotionValidationError(`Unknown promotion mode: ${String(group.mode)}`);
    }
  }

  return mergeResolvedByProduct(resolved);
}

export type CreatePromotionDefinitionInput = {
  organizationId: string;
  promoProductId: string;
  isActive?: boolean;
  groups: Array<{
    mode: PromotionGroupMode;
    requiredUnits: number;
    /** Optional label shown to the cashier in the selection dialog. */
    label?: string;
    options: Array<{
      componentProductId: string;
      defaultQuantity?: number;
    }>;
  }>;
};

function mapOption(row: ProductPromotionOptionRow): PromotionOption {
  return {
    id: row.id,
    groupId: row.group_id,
    componentProductId: row.component_product_id,
    defaultQuantity: Number(row.default_quantity ?? 0),
  };
}

function mapGroup(row: ProductPromotionGroupRow, options: PromotionOption[]): PromotionGroup {
  return {
    id: row.id,
    productPromotionId: row.product_promotion_id,
    label: row.label ?? undefined,
    mode: row.mode,
    requiredUnits: Number(row.required_units ?? 1),
    options,
  };
}

export function mapProductPromotionRow(row: ProductPromotionRow): ProductPromotion {
  const rawGroups = row.product_promotion_groups ?? [];
  const groups: PromotionGroup[] = rawGroups
    .filter((g): g is ProductPromotionGroupRow => g != null)
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((g) => {
      const rawOpts = g.product_promotion_options ?? [];
      const opts = rawOpts
        .filter((o): o is ProductPromotionOptionRow => o != null)
        .sort((a, b) => a.id.localeCompare(b.id))
        .map(mapOption);
      return mapGroup(g, opts);
    });

  return {
    id: row.id,
    organizationId: row.organization_id,
    promoProductId: row.promo_product_id,
    isActive: Boolean(row.is_active ?? true),
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
    groups,
  };
}
